import { TextCodes } from '@api/types/translation/TextCodes';
import { GOOGLE_MAP_PROPS, SearchDealerOption, useFindDealerContext } from '@components';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, styled, TextField } from '@mui/material';
import { t } from '@translations';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import usePlacesAutocomplete from 'use-places-autocomplete';

import { parseDealerData, parsePlaceData } from './FindDealer.config';

const LazyFindDealerSearchContent = lazy(() =>
  import('@components').then((module) => ({
    default: module.FindDealerSearchContent,
  }))
);

const LazyAutocomplete = lazy(() =>
  import('@mui/material/Autocomplete').then((module) => ({
    default: module.default as React.ComponentType<
      React.ComponentProps<typeof module.default<any, boolean, boolean, boolean>>
    >,
  }))
);

const StyledTextField = styled(TextField)`
  & .MuiInputBase-input {
    box-sizing: border-box;
  }
`;

export const FindDealerSearch = () => {
  const { map, currentDealerList, updateSearchValue, searchValue, resetLocation } =
    useFindDealerContext();
  const {
    ready,
    value,
    setValue,
    suggestions: { data, loading },
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: {
      types: ['geocode'],
      componentRestrictions: { country: 'NO' },
      bounds: GOOGLE_MAP_PROPS.bounds,
    },
  });
  const [combinedData, setCombinedData] = useState<SearchDealerOption[]>([]);

  const updateMapViewport = useCallback(
    (lat: number, lng: number, zoom: number) => {
      map?.panTo({ lat, lng });
      map?.setZoom(zoom);
    },
    [map]
  );

  const handleSelection = useCallback(
    (newValue: SearchDealerOption, zoom: number) => {
      updateMapViewport(newValue.lat, newValue.lng, zoom);
      updateSearchValue(newValue);
      resetLocation();
    },
    [updateMapViewport, updateSearchValue, resetLocation]
  );

  const handleNoSelection = useCallback(() => {
    updateMapViewport(
      GOOGLE_MAP_PROPS.center.lat,
      GOOGLE_MAP_PROPS.center.lng,
      GOOGLE_MAP_PROPS.minzoom
    );
    clearSuggestions();
    updateSearchValue(null);
  }, [clearSuggestions, updateMapViewport, updateSearchValue]);

  const handleAutocompleteChange = useCallback(
    async (event: React.SyntheticEvent<Element, Event>, newValue: SearchDealerOption | null) => {
      if (!newValue) return handleNoSelection();
      if (newValue.type === 'place')
        return handleSelection(newValue as SearchDealerOption, GOOGLE_MAP_PROPS.midzoom);
      if (newValue.type === 'dealer')
        return handleSelection(newValue as SearchDealerOption, GOOGLE_MAP_PROPS.maxzoom);
    },
    [handleNoSelection, handleSelection]
  );

  const groupByOption = (option: SearchDealerOption) => {
    if (option.type === 'dealer') return t(TextCodes.Dealer.Find.Dealer);
    if (option.type === 'place') return t(TextCodes.Dealer.Find.Places);
    return '';
  };

  const handleInputChanges = (
    event: React.SyntheticEvent<Element, Event>,
    newInputValue: string
  ) => {
    setValue(newInputValue);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const parsedDealers = (currentDealerList || []).map(parseDealerData);
        const parsedPlaces = await Promise.all(data.map(parsePlaceData));
        setCombinedData([...parsedPlaces, ...parsedDealers]);
      } catch (error) {
        console.error('Error parsing data:', error);
      }
    };

    fetchData();
  }, [currentDealerList, data]);

  return (
    <Suspense>
      <LazyAutocomplete
        disabled={!ready}
        sx={{ width: '100%' }}
        disablePortal
        id='dealer-search'
        options={combinedData}
        onChange={handleAutocompleteChange}
        onInputChange={handleInputChanges}
        value={searchValue}
        noOptionsText={
          loading ? t(TextCodes.Dealer.Find.Loading) : t(TextCodes.Dealer.Find.NoResults)
        }
        clearOnBlur={false}
        isOptionEqualToValue={(option: SearchDealerOption, value: SearchDealerOption) => {
          return option.id === value.id;
        }}
        slotProps={{
          popper: {
            sx: {
              zIndex: 100,
            },
          },
        }}
        getOptionLabel={(option: SearchDealerOption) => option.name}
        groupBy={groupByOption}
        filterOptions={(options, params) => {
          const inputValue = params.inputValue.toLowerCase();
          if (inputValue === '' || inputValue.length < 1) {
            return [];
          }
          const filteredOptions = options?.filter((option) => {
            if (option.type === 'dealer') {
              return (
                option.address.toLowerCase().includes(inputValue) ||
                option.city.toLowerCase().includes(inputValue) ||
                option.name.toLowerCase().includes(inputValue)
              );
            }
            if (option.type === 'place') {
              return option.name.toLowerCase().includes(inputValue);
            }
            return false;
          });

          return filteredOptions;
        }}
        renderOption={(props, option: SearchDealerOption) => (
          <li {...props} key={option?.id}>
            <Box display={'flex'} flexDirection={'column'}>
              <Suspense fallback={null}>
                <LazyFindDealerSearchContent option={option} value={value} />
              </Suspense>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            type='text'
            variant='outlined'
            placeholder={t(TextCodes.Dealer.Find.Search)}
          />
        )}
      />
    </Suspense>
  );
};
